/**
 * GuestLinkControl — facilitator affordance to turn on / copy / revoke the
 * account-less guest link for a session (migration 660).
 *
 * Mounted by GroupSetupView (group sessions) and the Session app's host
 * console. Turning the link on is one act on the server,
 * `enable_session_guest_link` (migration 960): the facilitator or creator only;
 * it keeps the session's `group_join_token` (a group session mints one at
 * creation, migration 455) or mints one for any other session, then enables
 * guest access with the chosen expiry.
 *
 * Turn on / Re-enable is drawn only for those two people (build contract §2.8);
 * anyone else sees a line naming who may (`guestLinkEnableAllowed`).
 *
 * Revoking and turning the link off still write `guest_token_revoked_at` /
 * `guest_access_enabled` directly under the workspace UPDATE RLS policy
 * (migration 554); see scripts/oneWritePathAllowlist.txt.
 */

import { Select } from '@/components/ds';
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import { useUserStore } from '../../stores/prioritization/userStore';
import {
  buildGuestJoinUrl,
  computeGuestTokenExpiresAt,
  deriveGuestLinkStatus,
  GUEST_LINK_ENABLE_LINE,
  GUEST_LINK_EXPIRY_CHOICES,
  guestLinkEnableAllowed,
  type GuestLinkExpiryChoice,
} from './guestLinkControl.helpers';

interface GuestLinkControlProps {
  sessionId: string;
}

interface GuestLinkRow {
  group_join_token: string | null;
  guest_access_enabled: boolean;
  guest_token_expires_at: string | null;
  guest_token_revoked_at: string | null;
  facilitator_id: string | null;
  created_by: string | null;
}

const label: CSSProperties = {
  fontFamily: 'var(--mono)',
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--text-ter)',
};

const primaryBtn: CSSProperties = {
  background: 'var(--mustard)',
  color: '#0B0F19',
  border: 'none',
  borderRadius: '8px',
  fontFamily: 'var(--sans)',
  fontSize: '13px',
  fontWeight: 700,
  padding: '8px 16px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const ghostBtn: CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--rule-bright)',
  borderRadius: '8px',
  color: 'var(--text-sec)',
  fontFamily: 'var(--sans)',
  fontSize: '13px',
  padding: '7px 14px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

export function GuestLinkControl({ sessionId }: GuestLinkControlProps) {
  const [row, setRow] = useState<GuestLinkRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [expiryChoice, setExpiryChoice] = useState<GuestLinkExpiryChoice>('none');
  const userId = useUserStore((s) => s.userId);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError(null);
    const supabase = getTypedSupabaseClient();
    const { data, error: fetchError } = await supabase
      .from('prioritization_sessions')
      .select('group_join_token, guest_access_enabled, guest_token_expires_at, guest_token_revoked_at, facilitator_id, created_by')
      .eq('id', sessionId)
      .maybeSingle();
    if (fetchError) {
      setError(fetchError.message);
      setLoading(false);
      return;
    }
    setRow((data as GuestLinkRow | null) ?? null);
    setLoading(false);
  }, [sessionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = useCallback(
    async (fields: Partial<Pick<GuestLinkRow, 'guest_access_enabled' | 'guest_token_revoked_at' | 'guest_token_expires_at'>>) => {
      if (!row) return;
      setSaving(true);
      setError(null);
      const supabase = getTypedSupabaseClient();
      const { error: updateError } = await supabase
        .from('prioritization_sessions')
        .update(fields)
        .eq('id', sessionId);
      setSaving(false);
      if (updateError) {
        // No optimistic state: only reflect the write once it's confirmed.
        setError(updateError.message);
        return;
      }
      setRow({ ...row, ...fields });
    },
    [row, sessionId],
  );

  if (loading) {
    return (
      <div>
        <span style={label}>Guest link (no account required)</span>
        <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: 'var(--text-ter)', marginTop: '8px' }}>
          Loading…
        </p>
      </div>
    );
  }

  // A session with no token yet (anything but a group session) reads as off:
  // turning it on mints the token.
  const joinToken = row?.group_join_token ?? null;
  const status = joinToken
    ? deriveGuestLinkStatus({
        guestAccessEnabled: row!.guest_access_enabled,
        guestTokenRevokedAt: row!.guest_token_revoked_at,
        guestTokenExpiresAt: row!.guest_token_expires_at,
        now: new Date(),
      })
    : 'off';
  const guestUrl = joinToken ? buildGuestJoinUrl(window.location.origin, joinToken) : '';
  // enable_session_guest_link (960) accepts the facilitator or the creator only.
  const mayEnable = guestLinkEnableAllowed({
    userId,
    facilitatorId: row?.facilitator_id ?? null,
    createdBy: row?.created_by ?? null,
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(guestUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(`Could not copy — copy manually: ${guestUrl}`);
    }
  };

  const handleEnable = async () => {
    setSaving(true);
    setError(null);
    const { error: rpcError } = await getTypedSupabaseClient().rpc('enable_session_guest_link', {
      p_session_id: sessionId,
      ...(expiryChoice !== 'none' && { p_expires_at: computeGuestTokenExpiresAt(expiryChoice, new Date())! }),
    });
    setSaving(false);
    if (rpcError) {
      setError(rpcError.message);
      return;
    }
    await load(true);
  };

  const handleRevoke = () => patch({ guest_token_revoked_at: new Date().toISOString() });

  const handleTurnOff = () => patch({ guest_access_enabled: false });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <span style={label}>Guest link (no account required)</span>
        <span
          style={{
            fontFamily: 'var(--mono)',
            fontSize: '11px',
            padding: '2px 8px',
            borderRadius: '999px',
            color:
              status === 'active' ? 'var(--green)' : status === 'off' ? 'var(--text-ter)' : 'var(--coral)',
            background:
              status === 'active' ? 'var(--green-dim)' : status === 'off' ? 'var(--hover-tint)' : 'var(--coral-dim)',
          }}
        >
          {status === 'active' && 'Active'}
          {status === 'off' && 'Off'}
          {status === 'revoked' && 'Revoked'}
          {status === 'expired' && 'Expired'}
        </span>
      </div>

      <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: 'var(--text-sec)', marginTop: '6px', marginBottom: '10px', lineHeight: 1.5 }}>
        People with this link can add a name and weigh in on the active activity — no account. They
        cannot open, close, publish, or ratify anything.
      </p>

      {status === 'active' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <code
            style={{
              flex: 1,
              minWidth: '200px',
              fontFamily: 'var(--mono)',
              fontSize: '12px',
              color: 'var(--text-pri)',
              background: 'var(--bg-raised)',
              border: '1px solid var(--rule-bright)',
              borderRadius: '6px',
              padding: '8px 10px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {guestUrl}
          </code>
          <button type="button" style={primaryBtn} onClick={() => void handleCopy()} disabled={saving}>
            {copied ? 'Copied!' : 'Copy link'}
          </button>
          <button type="button" style={ghostBtn} onClick={() => void handleRevoke()} disabled={saving}>
            Revoke
          </button>
          <button type="button" style={ghostBtn} onClick={() => void handleTurnOff()} disabled={saving}>
            Turn off
          </button>
        </div>
      ) : !mayEnable ? (
        <p style={{ fontFamily: 'var(--sans)', fontSize: '13px', color: 'var(--text-ter)', margin: 0 }}>
          {GUEST_LINK_ENABLE_LINE}
        </p>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Select
            aria-label="Guest link expiry"
            value={expiryChoice}
            onChange={(e) => setExpiryChoice(e.target.value as GuestLinkExpiryChoice)}
            disabled={saving}
          >
            {GUEST_LINK_EXPIRY_CHOICES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
          <button type="button" style={primaryBtn} onClick={() => void handleEnable()} disabled={saving}>
            {saving ? 'Turning on…' : status === 'off' ? 'Turn on guest link' : 'Re-enable link'}
          </button>
        </div>
      )}

      {status !== 'off' && status !== 'active' && mayEnable && (
        <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: 'var(--text-ter)', marginTop: '8px' }}>
          Guests cannot join or submit while the link is {status}. Turn it back on above.
        </p>
      )}

      {error && (
        <p style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: 'var(--coral)', marginTop: '8px' }}>
          {error}
        </p>
      )}
    </div>
  );
}
